import {
  RecurringSettingsSeparator,
  RecurringSettingsMonthRepeatMode,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import {
  Form,
  Space,
  InputNumber,
  Select,
  Checkbox,
  Radio,
  Button,
} from "antd";
import { useIntl, FormattedMessage } from "react-intl";
import format from "date-fns/format";
import DatePickerField from "components/fields/DatePickerField";
import { ROUTE_PATHS } from "const/paths";
import { useMatch } from "react-router-dom";
import MainCreationForm from "components/forms/MainCreationForm";

import { RecurringSettingsFieldProps } from "../../types";
import { useEventDateWeekdayData } from "../../hooks";

import { useWeekDays, useWeekdaysValidator } from "./hooks";
import { RecurringMode } from "./enums";
import { RecurringSettingsFormProps } from "./types";

const RecurringSettingsForm: React.FC<RecurringSettingsFormProps> = ({
  initialValues,
  onFinish,
  onClose,
  eventDate,
  fields,
}) => {
  const intl = useIntl();
  const isDetailsRoute = !!useMatch(ROUTE_PATHS.RecuringEventDetails);
  const weekdaysOptions = useWeekDays();
  const [form] =
    Form.useForm<Exclude<RecurringSettingsFieldProps["value"], undefined>>();
  const activeMode = Form.useWatch("mode", form);
  const separator = Form.useWatch("separator", form);
  const weekdays = [
    Form.useWatch("isOnSunday", form) || false,
    Form.useWatch("isOnMonday", form) || false,
    Form.useWatch("isOnTuesday", form) || false,
    Form.useWatch("isOnWednesday", form) || false,
    Form.useWatch("isOnThursday", form) || false,
    Form.useWatch("isOnFriday", form) || false,
    Form.useWatch("isOnSaturday", form) || false,
  ];
  const weekdaysValidator = useWeekdaysValidator(weekdays);
  const separatorCount = Form.useWatch("separatorCount", form);
  const monthDay = format(eventDate, "d");
  const weekday = format(eventDate, "EEEE");
  const { isLastWeek, weekOfMonth } = useEventDateWeekdayData(eventDate);

  return (
    <MainCreationForm {...{ form, initialValues, onFinish, fields }}>
      <Space direction="vertical">
        <FormattedMessage defaultMessage="Repeat every" />
        <Space>
          <Form.Item
            name="separatorCount"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber
              min={1}
              precision={0}
              placeholder={intl.formatMessage({
                defaultMessage: "Period frequency",
              })}
            />
          </Form.Item>
          <Form.Item
            name="separator"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              placeholder={intl.formatMessage({
                defaultMessage: "Period",
              })}
            >
              <Select.Option value={RecurringSettingsSeparator.DAILY}>
                <FormattedMessage
                  values={{ separatorCount }}
                  defaultMessage="{separatorCount, plural, one {Day} other {Days}}"
                />
              </Select.Option>
              <Select.Option value={RecurringSettingsSeparator.WEEKLY}>
                <FormattedMessage
                  values={{ separatorCount }}
                  defaultMessage="{separatorCount, plural, one {Week} other {Weeks}}"
                />
              </Select.Option>
              <Select.Option value={RecurringSettingsSeparator.MONTHLY}>
                <FormattedMessage
                  values={{ separatorCount }}
                  defaultMessage="{separatorCount, plural, one {Month} other {Months}}"
                />
              </Select.Option>
              <Select.Option value={RecurringSettingsSeparator.YEARLY}>
                <FormattedMessage
                  values={{ separatorCount }}
                  defaultMessage="{separatorCount, plural, one {Year} other {Years}}"
                />
              </Select.Option>
            </Select>
          </Form.Item>
        </Space>
        {separator === RecurringSettingsSeparator.WEEKLY && (
          <>
            <FormattedMessage defaultMessage="Repeat on" />
            <Form.Item
              getValueFromEvent={() => undefined}
              name="weekdays"
              rules={[
                {
                  validator: weekdaysValidator,
                },
              ]}
            >
              <Space>
                {weekdaysOptions.map((option) => (
                  <Form.Item
                    key={option.name?.toString()}
                    name={option.name}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>{option.label}</Checkbox>
                  </Form.Item>
                ))}
              </Space>
            </Form.Item>
          </>
        )}
        {separator === RecurringSettingsSeparator.MONTHLY && (
          <Form.Item
            name="monthRepeatMode"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              placeholder={intl.formatMessage({
                defaultMessage: "Repeat mode",
              })}
            >
              <Select.Option value={RecurringSettingsMonthRepeatMode.MONTH_DAY}>
                <FormattedMessage
                  values={{ monthDay }}
                  defaultMessage="Every {monthDay}. day of month"
                />
              </Select.Option>
              <Select.Option value={RecurringSettingsMonthRepeatMode.WEEK_DAY}>
                <FormattedMessage
                  values={{ weekday, weekOfMonth }}
                  defaultMessage="Every {weekOfMonth}. {weekday}"
                />
              </Select.Option>
              {isLastWeek && (
                <Select.Option
                  value={RecurringSettingsMonthRepeatMode.LAST_WEEK_DAY}
                >
                  <FormattedMessage
                    values={{ weekday }}
                    defaultMessage="Every last {weekday}"
                  />
                </Select.Option>
              )}
            </Select>
          </Form.Item>
        )}
        <FormattedMessage defaultMessage="Ends" />
        <Form.Item name="mode">
          <Radio.Group>
            <Space direction="vertical">
              <Space>
                <Radio value={RecurringMode.END_AT}>
                  <FormattedMessage defaultMessage="On" />
                </Radio>
                <Form.Item
                  noStyle
                  name="endAt"
                  rules={
                    activeMode !== RecurringMode.END_AT
                      ? undefined
                      : [{ required: true }]
                  }
                >
                  <DatePickerField
                    disabled={
                      activeMode !== RecurringMode.END_AT || isDetailsRoute
                    }
                    showTime
                    utc
                  />
                </Form.Item>
              </Space>
              <Space>
                <Radio value={RecurringMode.MAX_OCCURRENCES}>
                  <FormattedMessage defaultMessage="After" />
                </Radio>
                <Form.Item noStyle name="maxOccurrences">
                  <InputNumber
                    addonAfter={intl.formatMessage({
                      defaultMessage: "Occurrences",
                    })}
                    min={1}
                    precision={0}
                    disabled={activeMode !== RecurringMode.MAX_OCCURRENCES}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Maximum number of occurrences",
                    })}
                  />
                </Form.Item>
              </Space>
            </Space>
          </Radio.Group>
        </Form.Item>
        <Space>
          <Button type="default" htmlType="button" onClick={onClose}>
            <FormattedMessage defaultMessage="Close" />
          </Button>
          <Button type="primary" htmlType="submit">
            <FormattedMessage defaultMessage="Confirm" />
          </Button>
        </Space>
      </Space>
    </MainCreationForm>
  );
};

export default RecurringSettingsForm;
